import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Category } from '../../types/category';
import { Trash2, ChevronLeft, ChevronRight, ImageIcon, Edit2 } from 'lucide-react';
import Button from '../../components/Button';
import GlassPanel from '../../components/GlassPanel';
import { motion, AnimatePresence } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import { categoryApi } from '../../services/categoryApi';
import { useTheme } from '../../context/ThemeContext';
import { getUserRole } from '../../utils/auth';

const CategoryManagement: React.FC = () => {
  const { theme } = useTheme();
  const [isAdmin, setIsAdmin] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [operationResult, setOperationResult] = useState<{ status: number; message: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  
  const [newCategory, setNewCategory] = useState<Omit<Category, 'categoryId'>>({
    categoryName: '',
    description: '',
    thumbnailUrl: '',
    numberOfGamesLeft: 0
  });
  const [filePreview, setFilePreview] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editFilePreview, setEditFilePreview] = useState<string>('');
  const [editSelectedFile, setEditSelectedFile] = useState<File | null>(null);
  
  const itemsPerPage = 6;

  // Check if user is admin
  useEffect(() => {
    const { isAdmin: admin } = getUserRole();
    setIsAdmin(admin);
  }, []);

  const fetchCategories = useCallback(async (page: number) => {
    if (!isAdmin) return; // Don't fetch categories if not admin
    
    const abortController = new AbortController();
    try {
      setIsLoading(true);
      const response = await categoryApi.getAllCategories(page, itemsPerPage);
      if (response) {
        setCategories(response.content);
        setTotalPages(response.totalPages);
        setTotalItems(response.totalElements);
      }
    } catch (error: any) {
      if (!abortController.signal.aborted) {
        setOperationResult({
          status: error.response?.status || 500,
          message: error.response?.data?.message || 'فشل في جلب الفئات'
        });
      }
    } finally {
      setIsLoading(false);
    }
    return () => abortController.abort();
  }, [itemsPerPage, isAdmin]);

  useEffect(() => {
    fetchCategories(currentPage);
  }, [currentPage, fetchCategories]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: useCallback((acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file && file.size <= 5500000) {
        setSelectedFile(file);
        setFilePreview(URL.createObjectURL(file));
      }
    }, []),
    accept: { 'image/*': [] },
    maxSize: 5500000,
    multiple: false
  });

  const { 
    getRootProps: getEditRootProps, 
    getInputProps: getEditInputProps, 
    isDragActive: isEditDragActive 
  } = useDropzone({
    onDrop: useCallback((acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file && file.size <= 5500000) {
        setEditSelectedFile(file);
        setEditFilePreview(URL.createObjectURL(file));
      }
    }, []),
    accept: { 'image/*': [] },
    maxSize: 5500000,
    multiple: false
  });

  const PaginationControls = useMemo(() => (
    <div className="flex justify-center items-center gap-2 mt-4">
      <Button
        variant="secondary"
        onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
        disabled={currentPage === 0}
        icon={<ChevronLeft className="w-3 h-3" />}
        className="text-xs py-1 px-2"
      >
        السابق
      </Button>
      <span className={`text-xs ${theme === 'dark' ? 'text-white' : 'text-[#2D3748]'}`}>
        صفحة {currentPage + 1} من {totalPages}
      </span>
      <Button
        variant="secondary"
        onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
        disabled={currentPage === totalPages - 1}
        icon={<ChevronRight className="w-3 h-3" />}
        className="text-xs py-1 px-2"
      >
        التالي
      </Button>
    </div>
  ), [currentPage, totalPages, theme]);

  const handleDeleteCategory = useCallback(async (categoryId: string) => {
    if (!isAdmin) {
      setOperationResult({ status: 403, message: 'ليس لديك صلاحية لحذف الفئات' });
      return;
    }

    if (!window.confirm('حذف هذه الفئة؟')) return;

    try {
      setIsLoading(true);
      await categoryApi.deleteCategory(categoryId);
      setOperationResult({ status: 200, message: 'تم حذف الفئة' });
      
      if (categories.length === 1 && currentPage > 0) {
        setCurrentPage(p => p - 1);
      } else {
        fetchCategories(currentPage);
      }
    } catch (error: any) {
      setOperationResult({
        status: error.response?.status || 500,
        message: error.response?.data?.message || 'فشل الحذف'
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, categories.length, fetchCategories, isAdmin]);

  const handleSubmitCategory = useCallback(async () => {
    if (!isAdmin) {
      setOperationResult({ status: 403, message: 'ليس لديك صلاحية لإنشاء فئات جديدة' });
      return;
    }

    try {
      setIsLoading(true);
      const response = await categoryApi.createCategory(newCategory || undefined);
      if (selectedFile && response.categoryId) {
        await categoryApi.uploadCategoryThumbnail(response?.categoryId || '', selectedFile);
      }  
      setOperationResult({ status: 200, message: 'تم إنشاء الفئة' });
      
      setSelectedFile(null);
      setFilePreview('');
      setNewCategory({ categoryName: '', description: '', thumbnailUrl: '', numberOfGamesLeft: 0 });
      fetchCategories(currentPage);
    } catch (error: any) {
      setOperationResult({
        status: error.response?.status || 500,
        message: error.response?.data?.message || 'فشل الإنشاء'
      });
    } finally {
      setIsLoading(false);
    }
  }, [newCategory, selectedFile, currentPage, fetchCategories, isAdmin]);

  const handleEditCategory = useCallback((category: Category) => {
    if (!isAdmin) {
      setOperationResult({ status: 403, message: 'ليس لديك صلاحية لتعديل الفئات' });
      return;
    }

    setEditingCategory(category);
    setEditFilePreview(category.thumbnailUrl || '');
    setIsEditMode(true);
  }, [isAdmin]);

  const handleUpdateCategory = useCallback(async () => {
    if (!isAdmin) {
      setOperationResult({ status: 403, message: 'ليس لديك صلاحية لتحديث الفئات' });
      return;
    }
    
    if (!editingCategory || !editingCategory.categoryId) return;
    
    try {
      setIsLoading(true);
      await categoryApi.updateCategory(editingCategory.categoryId, {
        categoryName: editingCategory.categoryName,
        description: editingCategory.description,
        thumbnailUrl: editingCategory.thumbnailUrl,
        numberOfGamesLeft: editingCategory.numberOfGamesLeft
      });
      
      if (editSelectedFile) {
        await categoryApi.uploadCategoryThumbnail(editingCategory.categoryId, editSelectedFile);
      }
      
      setOperationResult({ status: 200, message: 'تم تحديث الفئة' });
      setIsEditMode(false);
      setEditingCategory(null);
      setEditSelectedFile(null);
      setEditFilePreview('');
      fetchCategories(currentPage);
    } catch (error: any) {
      setOperationResult({
        status: error.response?.status || 500,
        message: error.response?.data?.message || 'فشل التحديث'
      });
    } finally {
      setIsLoading(false);
    }
  }, [editingCategory, editSelectedFile, currentPage, fetchCategories, isAdmin]);

  const closeEditModal = () => {
    setIsEditMode(false);
    setEditingCategory(null);
    setEditSelectedFile(null);
    setEditFilePreview('');
  };

  // If user is not admin, show access denied message
  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <GlassPanel className={`p-6 ${theme === 'dark' ? 'bg-gray-800/90' : 'bg-[#F7F9FB]/80'} backdrop-blur-sm shadow-lg rounded-lg border ${theme === 'dark' ? 'border-gray-700' : 'border-[#E8ECEF]'}`}>
          <div className={`p-3 rounded-lg text-center ${
            theme === 'dark' 
              ? 'bg-red-900/30 text-red-300 border border-red-800' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            <p className="font-medium">ليس لديك صلاحية للوصول إلى إدارة الفئات. هذه الوظيفة متاحة فقط للمشرفين.</p>
          </div>
        </GlassPanel>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto px-2">
      {/* Categories Display Section */}
      <GlassPanel variant="elevated" className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-[#2D3748]'}`}>
            الفئات المتاحة ({totalItems})
          </h2>
        </div>

        {isLoading && categories.length === 0 ? (
          <div className="flex justify-center py-6">
            <div className="w-6 h-6 border-3 border-t-blue-500 rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {categories.length === 0 ? (
              <div className={`text-center py-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                لم يتم العثور على فئات
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                <AnimatePresence>
                  {categories.map((category) => (
                    <motion.div
                      key={category.categoryId}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className={`relative overflow-hidden rounded-lg border ${
                        theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
                      } shadow-md text-sm`}
                    >
                      <div className="aspect-[4/3] overflow-hidden">
                        {category.thumbnailUrl ? (
                          <img
                            src={category.thumbnailUrl}
                            alt={category.categoryName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = 'https://via.placeholder.com/400x225?text=No+Image';
                            }}
                          />
                        ) : (
                          <div className={`w-full h-full flex items-center justify-center ${
                            theme === 'dark' ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                          }`}>
                            <ImageIcon className="w-8 h-8 opacity-50" />
                          </div>
                        )}
                      </div>
                      <div className="p-2">
                        <h3 className={`text-base font-semibold ${theme === 'dark' ? 'text-white' : 'text-[#2D3748]'} mb-1 truncate`}>
                          {category.categoryName}
                        </h3>
                        <p className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} mb-2 line-clamp-2 h-8 overflow-hidden`}>
                          {category.description || 'لا يوجد وصف متاح'}
                        </p>
                        <div className="flex justify-end items-center">
                          <div className="flex space-x-1">
                            <Button
                              variant="secondary"
                              onClick={() => handleEditCategory(category)}
                              icon={<Edit2 className="w-3 h-3" />}
                              className={`p-1 ${theme === 'dark' ? 'bg-blue-900/40 hover:bg-blue-800/60 text-blue-400' : 'bg-blue-50 hover:bg-blue-100 text-blue-600'}`}
                            >
                              <span className="sr-only">تعديل</span>
                            </Button>
                            <Button
                              variant="secondary"
                              onClick={() => handleDeleteCategory(category.categoryId || '')}
                              icon={<Trash2 className="w-3 h-3" />}
                              className={`p-1 ${theme === 'dark' ? 'bg-red-900/40 hover:bg-red-800/60 text-red-400' : 'bg-red-50 hover:bg-red-100 text-red-600'}`}
                            >
                              <span className="sr-only">حذف</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
            {totalPages > 1 && PaginationControls}
          </>
        )}
      </GlassPanel>

      {/* Form to Add New Category */}
      <GlassPanel variant="elevated" className="p-4 mt-4">
        <div className="flex justify-between items-center mb-3">
          <h2 className={`text-base font-bold ${theme === 'dark' ? 'text-white' : 'text-[#2D3748]'}`}>
            فئة جديدة
          </h2>
        </div>
        
        {operationResult && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-2 rounded-lg mb-3 text-center ${
              operationResult.status === 200 
                ? theme === 'dark' 
                  ? 'bg-green-500/20 text-green-200' 
                  : 'bg-green-100 text-green-800'
                : theme === 'dark'
                  ? 'bg-red-500/20 text-red-200'
                  : 'bg-red-100 text-red-800'
            }`}
          >
            <p className="font-medium text-xs">{operationResult.message}</p>
          </motion.div>
        )}

        <div className="space-y-3">
          <input
            type="text"
            placeholder="اسم الفئة"
            value={newCategory.categoryName}
            onChange={(e) => setNewCategory(prev => ({ ...prev, categoryName: e.target.value }))}
            className={`w-full rounded-lg p-2 text-xs border ${
              theme === 'dark'
                ? 'bg-[#2D3748] text-white border-[#4A5568] focus:ring-[#4B7BFF]'
                : 'bg-white text-[#2D3748] border-[#E8ECEF] focus:ring-[#4B7BFF]'
            } focus:outline-none focus:ring-1`}
          />
          <textarea
            placeholder="الوصف"
            value={newCategory.description}
            onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
            className={`w-full rounded-lg p-2 text-xs border resize-none ${
              theme === 'dark'
                ? 'bg-[#2D3748] text-white border-[#4A5568] focus:ring-[#4B7BFF]'
                : 'bg-white text-[#2D3748] border-[#E8ECEF] focus:ring-[#4B7BFF]'
            } focus:outline-none focus:ring-1`}
            rows={2}
          />
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-3 cursor-pointer transition-colors ${
              isDragActive
                ? theme === 'dark'
                  ? 'border-[#4B7BFF] bg-[#4B7BFF]/10'
                  : 'border-[#4B7BFF] bg-[#4B7BFF]/10'
                : theme === 'dark'
                  ? 'border-[#4A5568] hover:border-[#4B7BFF]/50'
                  : 'border-[#E8ECEF] hover:border-[#4B7BFF]/50'
            }`}
          >
            <input {...getInputProps()} />
            {filePreview ? (
              <div className="relative aspect-[4/3] rounded-lg overflow-hidden">
                <img src={filePreview} alt="معاينة" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-2">
                <ImageIcon className={`w-8 h-8 mb-1 ${theme === 'dark' ? 'text-[#A0AEC0]' : 'text-[#718096]'}`} />
                <p className={`text-xs text-center ${theme === 'dark' ? 'text-[#A0AEC0]' : 'text-[#718096]'}`}>
                  اسحب وأسقط صورة أو انقر للتحديد
                </p>
              </div>
            )}
          </div>
          <Button
            variant="primary"
            onClick={handleSubmitCategory}
            disabled={isLoading || !newCategory.categoryName}
            className="w-full text-xs py-2"
          >
            {isLoading ? 'جارٍ الإنشاء...' : 'إنشاء فئة'}
          </Button>
        </div>
      </GlassPanel>

      {/* Operation Result Toast */}
      {operationResult && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
            operationResult.status === 200
              ? theme === 'dark'
                ? 'bg-green-800 text-green-100'
                : 'bg-green-100 text-green-800'
              : theme === 'dark'
              ? 'bg-red-800 text-red-100'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {operationResult.message}
        </motion.div>
      )}

      {/* Edit Category Modal */}
      {isEditMode && editingCategory && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2"
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`rounded-lg w-full max-w-md p-4 m-2 border ${
                theme === 'dark'
                  ? 'bg-[#1A202C] border-[#2D3748]'
                  : 'bg-white border-[#E8ECEF]'
              }`}
            >
              <div className="flex justify-between items-center mb-3">
                <h2 className={`text-base font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-[#2D3748]'
                }`}>
                  تعديل الفئة
                </h2>
                <Button
                  variant="secondary"
                  onClick={closeEditModal}
                  className="p-1 text-xs"
                >
                  إغلاق
                </Button>
              </div>

              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="اسم الفئة"
                  value={editingCategory.categoryName}
                  onChange={(e) => setEditingCategory(prev => prev ? { ...prev, categoryName: e.target.value } : null)}
                  className={`w-full rounded-lg p-2 text-xs border ${
                    theme === 'dark'
                      ? 'bg-[#2D3748] text-white border-[#4A5568] focus:ring-[#4B7BFF]'
                      : 'bg-white text-[#2D3748] border-[#E8ECEF] focus:ring-[#4B7BFF]'
                  } focus:outline-none focus:ring-1`}
                />
                <textarea
                  placeholder="الوصف"
                  value={editingCategory.description}
                  onChange={(e) => setEditingCategory(prev => prev ? { ...prev, description: e.target.value } : null)}
                  className={`w-full rounded-lg p-2 text-xs border resize-none ${
                    theme === 'dark'
                      ? 'bg-[#2D3748] text-white border-[#4A5568] focus:ring-[#4B7BFF]'
                      : 'bg-white text-[#2D3748] border-[#E8ECEF] focus:ring-[#4B7BFF]'
                  } focus:outline-none focus:ring-1`}
                  rows={2}
                />
                <div
                  {...getEditRootProps()}
                  className={`border-2 border-dashed rounded-lg p-3 cursor-pointer transition-colors ${
                    isEditDragActive
                      ? theme === 'dark'
                        ? 'border-[#4B7BFF] bg-[#4B7BFF]/10'
                        : 'border-[#4B7BFF] bg-[#4B7BFF]/10'
                      : theme === 'dark'
                        ? 'border-[#4A5568] hover:border-[#4B7BFF]/50'
                        : 'border-[#E8ECEF] hover:border-[#4B7BFF]/50'
                  }`}
                >
                  <input {...getEditInputProps()} />
                  {editFilePreview ? (
                    <div className="relative aspect-[4/3] rounded-lg overflow-hidden">
                      <img src={editFilePreview} alt="معاينة" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-2">
                      <ImageIcon className={`w-8 h-8 mb-1 ${theme === 'dark' ? 'text-[#A0AEC0]' : 'text-[#718096]'}`} />
                      <p className={`text-xs text-center ${theme === 'dark' ? 'text-[#A0AEC0]' : 'text-[#718096]'}`}>
                        اسحب وأسقط صورة جديدة أو انقر للتحديد
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    onClick={handleUpdateCategory}
                    disabled={isLoading || !editingCategory.categoryName}
                    className="w-full text-xs py-2"
                  >
                    {isLoading ? 'جارٍ التحديث...' : 'تحديث الفئة'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={closeEditModal}
                    className="w-full text-xs py-2"
                  >
                    إلغاء
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
};

export default CategoryManagement;